import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { GURTBREITE_LABELS, PALLET_BOX_TYPE_LABELS, WERK_LABELS } from '../../constants/order-document-labels';
import { ILieferterminOverviewRow } from '../../models/order-document.model';
import { HelperService } from '../../services/helper.service';

/** Column keys the table can sort by; must match the backend's sort whitelist. */
export type DeliveryDateSortField = 'date' | 'isBinding' | 'auftragsNr' | 'kunde' | 'palletNumber' | 'gurtbreite' | 'werk' | 'label';

export interface DeliveryDateSort {
  field: DeliveryDateSortField;
  direction: 'asc' | 'desc';
}

/**
 * The Liefertermine table, shared by the production TV board (`size="tv"`) and the in-app
 * Liefertermine page (default `size="compact"`). Only the typography and paddings differ between
 * the two — the markup, states and cell formatting are identical, see the component SCSS.
 */
@Component({
  selector: 'app-delivery-date-table',
  standalone: true,
  templateUrl: './delivery-date-table.component.html',
  styleUrls: ['./delivery-date-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.size-tv]': "size() === 'tv'",
  },
})
export class DeliveryDateTableComponent {
  private readonly helperService = inject(HelperService);

  readonly rows = input.required<ILieferterminOverviewRow[]>();
  readonly loading = input(false);
  readonly error = input(false);
  /** 'compact' for the regular page, 'tv' for the large production board. */
  readonly size = input<'tv' | 'compact'>('compact');
  /** Makes rows clickable and emits `rowClick`; the TV board leaves this off. */
  readonly clickable = input(false);
  /** Current sort; when unset the headers are not sortable (TV board). */
  readonly sort = input<DeliveryDateSort | null>(null);

  /** Message for an empty result set — the TV board says "für heute", the page says "vorhanden". */
  readonly emptyMessage = input('Keine Liefertermine vorhanden.');

  readonly rowClick = output<ILieferterminOverviewRow>();
  readonly sortChange = output<DeliveryDateSortField>();

  readonly sortable = computed(() => this.sort() !== null);

  /** Column order and headings, shared by both variants. */
  readonly columns: { field: DeliveryDateSortField; label: string }[] = [
    { field: 'date', label: 'Liefertermin' },
    { field: 'auftragsNr', label: 'Auftrags-Nr.' },
    { field: 'kunde', label: 'Kunde' },
    { field: 'palletNumber', label: 'Pal./Box-Nr.' },
    { field: 'gurtbreite', label: 'Gurtbreite' },
    { field: 'werk', label: 'Werk' },
    { field: 'label', label: 'Infotext' },
  ];

  sortIcon(field: DeliveryDateSortField): string {
    const sort = this.sort();
    return this.helperService.getSortIcon(sort?.field ?? '', sort?.direction ?? '', field);
  }

  onSort(field: DeliveryDateSortField): void {
    if (this.sortable()) {
      this.sortChange.emit(field);
    }
  }

  onRowClick(row: ILieferterminOverviewRow): void {
    if (this.clickable()) {
      this.rowClick.emit(row);
    }
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  /** "Palette 1234" / "Box 1234", falling back to just the number (or '—') if the type is unset. */
  formatPalletBox(row: ILieferterminOverviewRow): string {
    if (!row.palletNumber) return '—';
    const typeLabel = row.palletBoxType ? PALLET_BOX_TYPE_LABELS[row.palletBoxType] : '';
    return typeLabel ? `${typeLabel} ${row.palletNumber}` : row.palletNumber;
  }

  formatGurtbreite(row: ILieferterminOverviewRow): string {
    return row.gurtbreite ? GURTBREITE_LABELS[row.gurtbreite] : '—';
  }

  formatWerk(row: ILieferterminOverviewRow): string {
    return row.werk ? WERK_LABELS[row.werk] : '—';
  }

  trackRow(index: number, row: ILieferterminOverviewRow): string {
    return `${row.orderDocumentId}-${new Date(row.date).getTime()}-${index}`;
  }
}
